import type { Dispatch, FC, SetStateAction } from 'react';
import type React from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import type { AxiosError } from 'axios';
import { usePopupState } from 'material-ui-popup-state/hooks';

import settings from 'src/settings';
import type { User, UserAdminRequestState } from 'src/types/user';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpServiceInstance from 'src/services/api/http';
import { routePaths } from 'src/services/routing/routePaths';
import { useConfirm } from 'src/components/app/context/ConfirmContext';
import { useFilterContext } from 'src/components/app/context/FilterContext';
import useUserAdminQuery from 'src/components/userAdmin/hooks/useUserAdminQuery';

export interface UserAdminContextValue {
  bulkEditDialogOpen: boolean;
  bulkEditingRequestState?: UserAdminRequestState;
  handleBulkEditDialogOpen: () => void;
  handleRemoveUser: (userId: string) => Promise<void>;
  isFullSelection: boolean;
  loading: boolean;
  page: number;
  pageSize: number;
  refetch: () => void;
  resetPageNumber: () => void;
  searchFilters: Record<string, string>[][];
  searchResults: User[];
  searchResultsCount: number;
  searchString: string;
  setBulkEditDialogOpen: (value: boolean) => void;
  setBulkEditingRequestState: (value?: UserAdminRequestState) => void;
  setIsFullSelection: (value: boolean) => void;
  setPageNumber: (pageNo: number) => void;
  setSearchString: (value: string) => void;
  setValidation: Dispatch<SetStateAction<string | null>>;
  submitSearch: () => void;
  validation: string | null;
}

export const UserAdminContext = createContext<UserAdminContextValue>({
  bulkEditDialogOpen: false,
  bulkEditingRequestState: { status: 'success' },
  handleBulkEditDialogOpen: () => {},
  handleRemoveUser: () => Promise.resolve(),
  isFullSelection: false,
  loading: false,
  page: 1,
  pageSize: 100,
  refetch: () => {},
  resetPageNumber: () => {},
  searchFilters: [],
  searchResults: [],
  searchResultsCount: 0,
  searchString: '',
  setBulkEditDialogOpen: () => {},
  setBulkEditingRequestState: () => {},
  setIsFullSelection: () => {},
  setPageNumber: () => {},
  setSearchString: () => {},
  setValidation: () => {},
  submitSearch: () => {},
  validation: null,
});

interface UserAdminProviderProps {
  children: React.ReactNode;
}

const UserAdminProvider: FC<UserAdminProviderProps> = ({ children }) => {
  const { t } = useTranslation('admin');
  const confirm = useConfirm();
  const history = useHistory();
  const { filterGroups } = useFilterContext();

  const [isFullSelection, setIsFullSelection] = useState(false);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [searchStringSubmitted, setSearchStringSubmitted] = useState('');
  const [validation, setValidation] = useState<string | null>(null);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState<User[]>([]);
  const [count, setCount] = useState(0);
  const [bulkEditingRequestState, setBulkEditingRequestState] = useState<
    UserAdminRequestState | undefined
  >(undefined);

  const userBulkEditPopupState = usePopupState({
    variant: 'popover',
    popupId: 'userBulkEditMenu',
  });

  const searchFilters = useMemo(
    () =>
      Object.values(filterGroups).map((filterGroups) =>
        filterGroups.map((filterValue) => filterValue.filterExpr),
      ),
    [filterGroups],
  );

  const pageSize = useMemo(
    () => Number(settings.VITE_APP_PAGINATION_THRESHOLD_USER_LIST) || 100,
    [settings],
  );

  const handleBulkEditDialogOpen = () => {
    userBulkEditPopupState.close();
    setBulkEditDialogOpen(true);
  };

  const setPageNumber = (pageNo: number) => {
    setPage(pageNo);
  };

  const resetPageNumber = () => {
    setPage(1);
  };

  const submitSearch = () => {
    if (searchString.trim().split(/\s+/).length > 5) {
      setValidation(t('admin:List.Only5WordsAllowed'));
    } else {
      setValidation(null);
      setSearchStringSubmitted(searchString);
    }
  };

  const { data, isLoading, isFetching, refetch } = useUserAdminQuery(
    searchStringSubmitted,
    page,
    searchFilters,
    pageSize,
  );

  useEffect(() => {
    resetPageNumber();
  }, [searchFilters]);

  useEffect(() => {
    if (isLoading || isFetching) return;

    const { users, count } = data || { users: [], count: 0 };
    setUsers(users);
    setCount(count);
  }, [data]);

  const handleRemoveUser = async (userId: string) => {
    await confirm({
      description: 'admin:Detail.Delete.Warning',
      title: 'admin:Detail.Delete.Headline',
    })
      .then(async () => {
        setIsLoadingDelete(true);
        await HttpServiceInstance.delete(
          `${apiPaths.ADMIN.USER}${userId}/`,
          {},
        );
      })
      .then(() => {
        history.push(routePaths.USER_ADMIN_LIST);
        refetch();
      })
      .catch((error: AxiosError) => error)
      .finally(() => setIsLoadingDelete(false));
  };

  return (
    <UserAdminContext.Provider
      value={{
        bulkEditDialogOpen,
        bulkEditingRequestState,
        handleBulkEditDialogOpen,
        handleRemoveUser,
        isFullSelection,
        loading: isLoadingDelete || isLoading || isFetching,
        page,
        pageSize,
        refetch,
        resetPageNumber,
        searchFilters,
        searchResults: users,
        searchResultsCount: count,
        searchString,
        setBulkEditDialogOpen,
        setBulkEditingRequestState,
        setIsFullSelection,
        setPageNumber,
        setSearchString,
        setValidation,
        submitSearch,
        validation,
      }}
    >
      {children}
    </UserAdminContext.Provider>
  );
};

export const useUserAdmin = () => useContext(UserAdminContext);

export default UserAdminProvider;
